import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "./SpotMarketTrades.css";

const DEFAULT_PAGE_LIMIT = 50;
const EXPORT_BATCH = 500;
const PAGE_LIMIT_OPTIONS = [20, 50, 100, 500, 1000];
const EXCEL_SHEET_TITLE = "Market Trades";

const formatDateTime = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return iso;
  }
};

const getCounterUser = (row) => {
  if (typeof row.counter_user === "string") return row.counter_user;
  return row.counter_user?.email || row.counter_user?.uuid || row.counter_user?.id || "";
};

const rowToExport = (row) => {
  const baseCurrency = row.base_currency || row.main_currency || row.base_currency_id;
  const quoteCurrency = row.quote_currency || row.currency || row.quote_currency_id;
  const feeCurrency = row.side === "BUY" ? baseCurrency : quoteCurrency;
  return {
    Time: row.time || row.createdAt || "",
    "Order ID": row.order_id || row.id || "",
    "User ID": row.user?.uuid || row.user?.id || "",
    Email: row.user?.email || row.user_email || "",
    Mobile: row.user?.phone || row.user_mobileNumber || "",
    Pair: baseCurrency && quoteCurrency ? `${baseCurrency}/${quoteCurrency}` : "",
    Side: row.side ?? "",
    "Order Type": row.order_type ?? "",
    Price: row.price ?? "",
    Quantity: row.quantity ?? "",
    Fee: row.fee ?? "",
    "Fee Currency": feeCurrency ?? "",
    "Counter User": getCounterUser(row),
  };
};

const SpotMarketTrades = () => {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(DEFAULT_PAGE_LIMIT);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [exportLoading, setExportLoading] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 1000);
    return () => clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, pageLimit]);

  const buildParams = useCallback(
    (pageNum = page, limitNum = pageLimit) => {
      const params = { page: pageNum, limit: limitNum };
      if (debouncedSearch) params.user = debouncedSearch;
      return params;
    },
    [page, pageLimit, debouncedSearch]
  );

  const fetchTrades = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await AdminService.getGlobalSpotTrades(buildParams());
      if (res?.success && Array.isArray(res.data)) {
        setTrades(res.data);
        setTotalPages(res.pagination?.total_pages || res.pagination?.totalPages || 1);
        setTotalCount(res.pagination?.total ?? res.data.length);
      } else {
        setTrades([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error("Failed to fetch global spot trades", err);
      setError("Failed to load trades from server.");
      setTrades([]);
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    fetchTrades();
  }, [fetchTrades]);

  const fetchAllFilteredTrades = async () => {
    let currentPage = 1;
    let lastPage = 1;
    const all = [];

    do {
      const res = await AdminService.getGlobalSpotTrades(buildParams(currentPage, EXPORT_BATCH));
      if (res?.success === false && !Array.isArray(res?.data)) {
        throw new Error(res?.message || "Failed to fetch data for export.");
      }
      const list = Array.isArray(res?.data) ? res.data : [];
      all.push(...list);
      lastPage = res?.pagination?.totalPages || res?.pagination?.total_pages || 1;
      currentPage += 1;
    } while (currentPage <= lastPage);

    return all;
  };

  const handleExportExcel = async () => {
    setExportLoading(true);
    try {
      const allRows = await fetchAllFilteredTrades();
      if (!allRows.length) {
        setToast({ type: "error", message: "No trades match your filters to export." });
        setTimeout(() => setToast(null), 3000);
        return;
      }

      const exportData = allRows.map(rowToExport);
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, EXCEL_SHEET_TITLE);
      const dateStamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `market_trades_${dateStamp}.xlsx`);

      setToast({ type: "success", message: `Exported ${allRows.length} trade(s) to Excel.` });
      setTimeout(() => setToast(null), 4000);
    } catch (e) {
      console.error(e);
      setToast({ type: "error", message: e?.message || "Failed to export Excel file." });
      setTimeout(() => setToast(null), 4000);
    } finally {
      setExportLoading(false);
    }
  };

  const handlePageLimitChange = (e) => {
    setPageLimit(Number(e.target.value) || DEFAULT_PAGE_LIMIT);
    setPage(1);
  };

  const pageStart = trades.length === 0 ? 0 : (page - 1) * pageLimit + 1;
  const pageEnd = trades.length === 0 ? 0 : (page - 1) * pageLimit + trades.length;

  return (
    <main className="dashboard-main dashboard-subpage">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Market Trades</span>
          </p>
          <h1 className="dashboard-hero__title">Trading Fee Report</h1>
          <p className="dashboard-hero__subtitle">
            View market trades and fee reports
          </p>
        </div>
        <div className="dashboard-hero__icon">
          <i className="fas fa-chart-line" />
        </div>
      </section>

      {toast && (
        <div className={`tradingfee-toast tradingfee-toast--${toast.type}`} role="status">
          <i className={`fas fa-${toast.type === "success" ? "check-circle" : "exclamation-circle"}`} />
          {toast.message}
        </div>
      )}

      <div className="dashboard-body">
        <section className="tradingfee-section">
          <div className="tradingfee-section__header">
            <div className="tradingfee-section__title-wrap">
              <span className="tradingfee-section__icon">
                <i className="fas fa-exchange-alt" />
              </span>
              <h2 className="tradingfee-section__title">Market Trades</h2>
            </div>
            <div className="tradingfee-section__stats tradingfee-section__stats--actions">
              <span className="tradingfee-stat">
                <strong>{trades.length}</strong> on page
                {totalCount > 0 && (
                  <>
                    {" "}
                    (<strong>{totalCount}</strong> total)
                  </>
                )}
              </span>
              {totalPages > 1 && (
                <span className="tradingfee-stat">
                  Page <strong>{page}</strong> / <strong>{totalPages}</strong>
                </span>
              )}
              <button
                type="button"
                className="tradingfee-export-btn"
                onClick={handleExportExcel}
                disabled={exportLoading || loading}
                title="Download filtered trades as Excel"
              >
                <i className={`fas ${exportLoading ? "fa-spinner fa-spin" : "fa-file-excel"}`} />
                {exportLoading ? "Exporting…" : "Download Excel"}
              </button>
              <button
                type="button"
                className="tradingfee-refresh-btn"
                onClick={fetchTrades}
                disabled={loading}
                title="Refresh"
              >
                <i className={`fas fa-sync-alt ${loading ? "fa-spin" : ""}`} />
                Refresh
              </button>
            </div>
          </div>
          {error && (
            <p className="tradingfee-error" style={{ color: "#dc2626", marginTop: "0.5rem" }}>
              {error}
            </p>
          )}
          <div className="tradingfee-search-wrap">
            <i className="fas fa-search" />
            <input
              type="text"
              placeholder="Search by order ID, email or pair..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="tradingfee-search-input"
            />
            <label className="tradingfee-page-limit">
              Rows per page
              <select
                value={pageLimit}
                onChange={handlePageLimitChange}
                className="tradingfee-search-input tradingfee-page-limit__select"
              >
                {PAGE_LIMIT_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="tradingfee-table-wrap">
            <table className="tradingfee-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Order ID</th>
                  <th>User ID</th>
                  <th> Email</th>
                  <th>Mobile</th>
                  <th>Pair</th>
                  <th>Side</th>
                  <th>Order Type</th>
                  <th>Price</th>
                  <th>Quantity</th>
                  <th>Fee</th>
                  <th>Counter User</th>
                </tr>
              </thead>
              <tbody>
                {loading && trades.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="tradingfee-empty">
                      <i className="fas fa-spinner fa-spin" /> Loading trades…
                    </td>
                  </tr>
                ) : trades.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="tradingfee-empty">
                      No trades match your search.
                    </td>
                  </tr>
                ) : (
                  trades.map((row) => {
                    const id = row.id || row._id || row.order_id;
                    const orderId = row.order_id || row.id;
                    const traderId = row.user?.id;
                    const userUuid = row.user?.uuid || "---";
                    const userEmail = row.user?.email || row.user_email || "---";
                    const userPhone = row.user?.phone || row.user_mobileNumber || "---";
                    const baseCurrency =
                      row.base_currency || row.main_currency || row.base_currency_id;
                    const quoteCurrency =
                      row.quote_currency || row.currency || row.quote_currency_id;
                    const counterUser = getCounterUser(row) || "---";

                    return (
                      <tr key={id}>
                        <td>{row.time || formatDateTime(row.createdAt)}</td>
                        <td>
                          <span className="tradingfee-order-id">{orderId}</span>
                        </td>
                        <td>
                          {traderId ? (
                            <Link
                              to={`/dashboard/trader/${traderId}`}
                              state={{ trader: row.user }}
                              className="tradingfee-user-link"
                              title="View trader details"
                            >
                              {userUuid}
                            </Link>
                          ) : (
                            userUuid
                          )}
                        </td>
                        <td>{userEmail}</td>
                        <td>{userPhone}</td>
                        <td>
                          <strong>
                            {baseCurrency}/{quoteCurrency}
                          </strong>
                        </td>
                        <td>
                          <span className={`tradingfee-side tradingfee-side--${row.side?.toLowerCase()}`}>
                            {row.side}
                          </span>
                        </td>
                        <td>{row.order_type}</td>
                        <td>
                          {typeof row.price === "number"
                            ? row.price.toLocaleString("en-IN", { minimumFractionDigits: 2 })
                            : row.price}
                        </td>
                        <td>
                          {typeof row.quantity === "number"
                            ? row.quantity.toLocaleString("en-IN", { maximumFractionDigits: 4 })
                            : row.quantity}
                        </td>
                        <td>
                          {typeof row.fee === "number"
                            ? row.fee.toLocaleString("en-IN", { minimumFractionDigits: 6 })
                            : row.fee}
                          &nbsp;{row.side === "BUY" ? baseCurrency : quoteCurrency}
                        </td>
                        <td>
                          {typeof row.counter_user === "object" && row.counter_user?.id ? (
                            <Link
                              to={`/dashboard/trader/${row.counter_user.id}`}
                              state={{ trader: row.counter_user }}
                              className="tradingfee-counter-link"
                              title="View counter trader details"
                            >
                              {counterUser}
                            </Link>
                          ) : (
                            <span className="tradingfee-counter">{counterUser}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {!loading && totalCount > 0 && (
            <div className="tradingfee-pagination">
              <span className="tradingfee-pagination__info">
                Showing {pageStart}–{pageEnd} of {totalCount}
                {totalPages > 1 && <> · Page {page} of {totalPages}</>}
              </span>
              {totalPages > 1 && (
                <div className="tradingfee-pagination__buttons">
                  <button
                    type="button"
                    className="tradingfee-pagination__btn"
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <i className="fas fa-chevron-left" /> Prev
                  </button>
                  <button
                    type="button"
                    className="tradingfee-pagination__btn"
                    disabled={page >= totalPages || loading}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next <i className="fas fa-chevron-right" />
                  </button>
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </main>
  );
};

export default SpotMarketTrades;
